import { useState, useCallback } from 'react';
import { performanceAPI } from '../../services/api/performance.api';
import useNotification from '../common/useNotification';

/**
 * Custom hook for performance appraisal management
 */
export const usePerformance = () => {
    const { showSuccess, showError } = useNotification();
    const [appraisals, setAppraisals] = useState([]);
    const [objectives, setObjectives] = useState([]);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 20,
        totalItems: 0,
        totalPages: 0,
        hasNextPage: false,
        hasPrevPage: false
    });
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(false);

    // ==================== APPRAISAL FUNCTIONS ====================

    /**
     * Fetch all appraisals with filtering
     */
    const fetchAppraisals = useCallback(async (params = {}) => {
        setLoading(true);
        try {
            const response = await performanceAPI.getAllAppraisals(params);
            
            if (response.data.success) {
                setAppraisals(response.data.data || []);
                setPagination(response.data.pagination || {});
                return response.data;
            } else {
                // Handle API response error
                setAppraisals([]);
                setPagination({});
                showError(response.data.message || 'Failed to fetch appraisals');
                return { success: false, data: [], pagination: {} };
            }
        } catch (error) {
            console.error('Error fetching appraisals:', error);
            setAppraisals([]);
            setPagination({});
            
            // Handle different types of errors
            if (error.response?.status === 401) {
                showError('Session expired. Please log in again.');
            } else if (error.response?.status === 403) {
                showError('You do not have permission to view appraisals.');
            } else if (error.response?.status >= 500) {
                showError('Server error. Please try again later.');
            } else {
                showError('Failed to fetch appraisals. Please check your connection.');
            }
            
            return { success: false, data: [], pagination: {}, error: error.message };
        } finally {
            setLoading(false);
        }
    }, [showError]);

    /**
     * Fetch current user's appraisals
     */
    const fetchMyAppraisals = useCallback(async (params = {}) => {
        setLoading(true);
        try {
            const response = await performanceAPI.getMyAppraisals(params);
            
            if (response.data.success) {
                setAppraisals(response.data.data || []);
                setPagination(response.data.pagination || {});
                return response.data;
            } else {
                setAppraisals([]);
                setPagination({});
                showError(response.data.message || 'Failed to fetch your appraisals');
                return { success: false, data: [], pagination: {} };
            }
        } catch (error) {
            console.error('Error fetching my appraisals:', error);
            setAppraisals([]);
            setPagination({});
            
            if (error.response?.status === 401) {
                showError('Session expired. Please log in again.');
            } else if (error.response?.status === 403) {
                showError('You do not have permission to view your appraisals.');
            } else if (error.response?.status >= 500) {
                showError('Server error. Please try again later.');
            } else {
                showError('Failed to fetch your appraisals. Please check your connection.');
            }
            
            return { success: false, data: [], pagination: {}, error: error.message };
        } finally {
            setLoading(false);
        }
    }, [showError]);

    /**
     * Fetch appraisals pending review
     */
    const fetchPendingReviews = useCallback(async (params = {}) => {
        setLoading(true);
        try {
            const response = await performanceAPI.getAppraisalsPendingReview(params);
            
            if (response.data.success) {
                setAppraisals(response.data.data || []);
                setPagination(response.data.pagination || {});
                return response.data;
            } else {
                setAppraisals([]);
                setPagination({});
                showError(response.data.message || 'Failed to fetch pending reviews');
                return { success: false, data: [], pagination: {} };
            }
        } catch (error) {
            console.error('Error fetching pending reviews:', error);
            setAppraisals([]);
            setPagination({});
            
            if (error.response?.status === 401) {
                showError('Session expired. Please log in again.');
            } else if (error.response?.status === 403) {
                showError('You do not have permission to view pending reviews.');
            } else if (error.response?.status >= 500) {
                showError('Server error. Please try again later.');
            } else {
                showError('Failed to fetch pending reviews. Please check your connection.');
            }
            
            return { success: false, data: [], pagination: {}, error: error.message };
        } finally {
            setLoading(false);
        }
    }, [showError]);

    /**
     * Fetch appraisal by ID
     */
    const fetchAppraisalById = useCallback(async (id) => {
        try {
            const response = await performanceAPI.getAppraisalById(id);
            
            if (response.data.success) {
                return response.data.data;
            }
            return null;
        } catch (error) {
            showError('Failed to fetch appraisal details');
            throw error;
        }
    }, [showError]);

    /**
     * Create new appraisal
     */
    const createAppraisal = useCallback(async (appraisalData) => {
        try {
            const response = await performanceAPI.createAppraisal(appraisalData);
            
            if (response.data.success) {
                showSuccess('Appraisal created successfully');
                return response.data.data;
            } else {
                showError(response.data.message || 'Failed to create appraisal');
                return null;
            }
        } catch (error) {
            console.error('Error creating appraisal:', error);
            
            if (error.response?.status === 401) {
                showError('Session expired. Please log in again.');
            } else if (error.response?.status === 403) {
                showError('You do not have permission to create appraisals.');
            } else if (error.response?.status === 400) {
                showError(error.response.data.message || 'Invalid data provided.');
            } else if (error.response?.status >= 500) {
                showError('Server error. Please try again later.');
            } else {
                showError('Failed to create appraisal. Please check your connection.');
            }
            
            return null;
        }
    }, [showSuccess, showError]);

    /**
     * Update appraisal
     */
    const updateAppraisal = useCallback(async (id, appraisalData) => {
        try {
            const response = await performanceAPI.updateAppraisal(id, appraisalData);
            
            if (response.data.success) {
                showSuccess('Appraisal updated successfully');
                return response.data.data;
            }
            return null;
        } catch (error) {
            showError('Failed to update appraisal');
            throw error;
        }
    }, [showSuccess, showError]);

    /**
     * Delete appraisal
     */
    const deleteAppraisal = useCallback(async (id) => {
        try {
            const response = await performanceAPI.deleteAppraisal(id);
            
            if (response.data.success) {
                showSuccess('Appraisal deleted successfully');
                // Remove from local state
                setAppraisals(prev => prev.filter(appraisal => appraisal.id !== id));
                return true;
            } else {
                showError(response.data.message || 'Failed to delete appraisal');
                return false;
            }
        } catch (error) {
            console.error('Error deleting appraisal:', error);
            
            if (error.response?.status === 401) {
                showError('Session expired. Please log in again.');
            } else if (error.response?.status === 403) {
                showError('You do not have permission to delete appraisals.');
            } else if (error.response?.status === 404) {
                showError('Appraisal not found.');
            } else if (error.response?.status >= 500) {
                showError('Server error. Please try again later.');
            } else {
                showError('Failed to delete appraisal. Please check your connection.');
            }
            
            return false;
        }
    }, [showSuccess, showError]);

    /**
     * Submit appraisal for review
     */
    const submitAppraisalForReview = useCallback(async (id, reviewData) => {
        try {
            const response = await performanceAPI.submitAppraisalForReview(id, reviewData);
            
            if (response.data.success) {
                showSuccess('Appraisal submitted for review');
                return response.data.data;
            }
            return null;
        } catch (error) {
            showError('Failed to submit appraisal for review');
            throw error;
        }
    }, [showSuccess, showError]);

    /**
     * Get appraisal statistics
     */
    const fetchAppraisalStats = useCallback(async (params = {}) => {
        try {
            const response = await performanceAPI.getAppraisalStats(params);
            
            if (response.data.success) {
                setStats(response.data.data);
                return response.data.data;
            }
            return null;
        } catch (error) {
            showError('Failed to fetch appraisal statistics');
            throw error;
        }
    }, [showError]);

    /**
     * Export appraisals
     */
    const exportAppraisals = useCallback(async (params = {}) => {
        try {
            const response = await performanceAPI.exportAppraisals(params);
            
            // Handle file download
            const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `appraisals-export-${new Date().toISOString().split('T')[0]}.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            
            showSuccess('Appraisals exported successfully');
            return true;
        } catch (error) {
            showError('Failed to export appraisals');
            throw error;
        }
    }, [showSuccess, showError]);

    // ==================== OBJECTIVE FUNCTIONS ====================

    /**
     * Fetch all objectives
     */
    const fetchObjectives = useCallback(async (params = {}) => {
        setLoading(true);
        try {
            const response = await performanceAPI.getAllObjectives(params);
            
            if (response.data.success) {
                setObjectives(response.data.data || []);
                setPagination(response.data.pagination || {});
                return response.data;
            } else {
                setObjectives([]);
                setPagination({});
                showError(response.data.message || 'Failed to fetch objectives');
                return { success: false, data: [], pagination: {} };
            }
        } catch (error) {
            console.error('Error fetching objectives:', error);
            setObjectives([]);
            setPagination({});
            
            if (error.response?.status === 401) {
                showError('Session expired. Please log in again.');
            } else if (error.response?.status === 403) {
                showError('You do not have permission to view objectives.');
            } else if (error.response?.status >= 500) {
                showError('Server error. Please try again later.');
            } else {
                showError('Failed to fetch objectives. Please check your connection.');
            }
            
            return { success: false, data: [], pagination: {}, error: error.message };
        } finally {
            setLoading(false);
        }
    }, [showError]);

    /**
     * Fetch current user's objectives
     */
    const fetchMyObjectives = useCallback(async (params = {}) => {
        setLoading(true);
        try {
            const response = await performanceAPI.getMyObjectives(params);
            
            if (response.data.success) {
                setObjectives(response.data.data || []);
                setPagination(response.data.pagination || {});
            }
            return response.data;
        } catch (error) {
            showError('Failed to fetch your objectives');
            throw error;
        } finally {
            setLoading(false);
        }
    }, [showError]);

    /**
     * Fetch objectives pending approval
     */
    const fetchPendingApprovals = useCallback(async (params = {}) => {
        setLoading(true);
        try {
            const response = await performanceAPI.getObjectivesPendingApproval(params);
            
            if (response.data.success) {
                setObjectives(response.data.data || []);
                setPagination(response.data.pagination || {});
            }
            return response.data;
        } catch (error) {
            showError('Failed to fetch pending approvals');
            throw error;
        } finally {
            setLoading(false);
        }
    }, [showError]);

    /**
     * Fetch objective by ID
     */
    const fetchObjectiveById = useCallback(async (id) => {
        try {
            const response = await performanceAPI.getObjectiveById(id);
            
            if (response.data.success) {
                return response.data.data;
            }
            return null;
        } catch (error) {
            showError('Failed to fetch objective details');
            throw error;
        }
    }, [showError]);

    /**
     * Create new objective
     */
    const createObjective = useCallback(async (objectiveData) => {
        try {
            const response = await performanceAPI.createObjective(objectiveData);
            
            if (response.data.success) {
                showSuccess('Objective created successfully');
                return response.data.data;
            }
            return null;
        } catch (error) {
            showError('Failed to create objective');
            throw error;
        }
    }, [showSuccess, showError]);

    /**
     * Update objective
     */
    const updateObjective = useCallback(async (id, objectiveData) => {
        try {
            const response = await performanceAPI.updateObjective(id, objectiveData);
            
            if (response.data.success) {
                showSuccess('Objective updated successfully');
                return response.data.data;
            }
            return null;
        } catch (error) {
            showError('Failed to update objective');
            throw error;
        }
    }, [showSuccess, showError]);

    /**
     * Delete objective
     */
    const deleteObjective = useCallback(async (id) => {
        try {
            const response = await performanceAPI.deleteObjective(id);
            
            if (response.data.success) {
                showSuccess('Objective deleted successfully');
                // Remove from local state
                setObjectives(prev => prev.filter(objective => objective.id !== id));
                return true;
            }
            return false;
        } catch (error) {
            showError('Failed to delete objective');
            throw error;
        }
    }, [showSuccess, showError]);

    /**
     * Get objective statistics
     */
    const fetchObjectiveStats = useCallback(async (params = {}) => {
        try {
            const response = await performanceAPI.getObjectiveStats(params);
            
            if (response.data.success) {
                return response.data.data;
            }
            return null;
        } catch (error) {
            showError('Failed to fetch objective statistics');
            throw error;
        }
    }, [showError]);

    return {
        // State
        appraisals,
        objectives,
        pagination,
        stats,
        loading,
        
        // Appraisal functions
        fetchAppraisals,
        fetchMyAppraisals,
        fetchPendingReviews,
        fetchAppraisalById,
        createAppraisal,
        updateAppraisal,
        deleteAppraisal,
        submitAppraisalForReview,
        fetchAppraisalStats,
        exportAppraisals,
        
        // Objective functions
        fetchObjectives,
        fetchMyObjectives,
        fetchPendingApprovals,
        fetchObjectiveById,
        createObjective,
        updateObjective,
        deleteObjective,
        fetchObjectiveStats
    };
};

export default usePerformance;